import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Popover } from 'antd';
import { useTranslate } from '@tolgee/react';
import HighlightText from './HighlightText';
import type { ContentParameter } from '../Types/api';

/** 匹配 @{xxx} 或 {{xxx}} */
const TEMPLATE_VAR_RE = /@\{([^}]+)\}|\{\{(.+?)\}\}/g;

interface Segment {
  type: 'text' | 'variable';
  value: string;
}

function parseTemplate(raw: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;

  for (const match of raw.matchAll(TEMPLATE_VAR_RE)) {
    const matchStart = match.index!;
    if (matchStart > lastIndex) {
      segments.push({ type: 'text', value: raw.slice(lastIndex, matchStart) });
    }
    segments.push({ type: 'variable', value: match[1] ?? match[2] });
    lastIndex = matchStart + match[0].length;
  }

  if (lastIndex < raw.length) {
    segments.push({ type: 'text', value: raw.slice(lastIndex) });
  }

  return segments;
}

interface TemplateTextProps {
  text: string;
  keyword?: string;
  parameterMap?: Record<string, ContentParameter>;
}

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#8A97AA',
  lineHeight: '22px',
};

const valueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#263246',
  lineHeight: '22px',
  wordBreak: 'break-word',
  minWidth: 0,
};

const contentGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(0, 1fr)',
  columnGap: 12,
  rowGap: 8,
  alignItems: 'start',
};

function VariablePopoverContent({ desc, example }: { desc?: string; example?: string }) {
  const { t } = useTranslate();

  return (
    <div
      style={{
        width: 312,
        ...contentGridStyle,
      }}
    >
      {desc ? (
        <>
          <span style={labelStyle}>{t('scene.aiAgentConfig.templateParameter.label.source')}</span>
          <span style={valueStyle}>{desc}</span>
        </>
      ) : null}

      {example ? (
        <>
          <span style={labelStyle}>{t('scene.aiAgentConfig.templateParameter.label.example')}</span>
          <span
            style={{
              display: 'inline-flex',
              width: 'fit-content',
              maxWidth: '100%',
              minHeight: 22,
              alignItems: 'center',
              borderRadius: 4,
              background: '#EEF3F8',
              padding: '1px 8px',
              color: '#263246',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: '20px',
              wordBreak: 'break-word',
            }}
          >
            {example}
          </span>
        </>
      ) : null}
    </div>
  );
}

function VariableLabel({ varName, param }: { varName: string; param?: ContentParameter }) {
  const label = param?.displayName || varName;
  const hasPopover = Boolean(param?.desc || param?.example);

  const node = (
    <span
      style={{
        color: 'var(--fett-scenario-accent-text)',
        cursor: hasPopover ? 'pointer' : undefined,
      }}
    >
      @
      <HighlightText text={label} />
    </span>
  );

  if (!hasPopover) return node;

  return (
    <Popover
      content={<VariablePopoverContent desc={param!.desc} example={param!.example} />}
      trigger="hover"
      placement="top"
      overlayInnerStyle={{ padding: 0 }}
      mouseEnterDelay={0.2}
    >
      {node}
    </Popover>
  );
}

export default function TemplateText({ text, keyword, parameterMap }: TemplateTextProps) {
  const segments = useMemo(() => parseTemplate(text), [text]);

  if (segments.length === 1 && segments[0].type === 'text') {
    return <HighlightText text={text} keyword={keyword} />;
  }

  const nodes: ReactNode[] = segments.map((seg, i) =>
    seg.type === 'variable' ? (
      <VariableLabel key={i} varName={seg.value} param={parameterMap?.[seg.value]} />
    ) : (
      <HighlightText key={i} text={seg.value} keyword={keyword} />
    ),
  );

  return <>{nodes}</>;
}
